'use client';

import {
  SavingsPlansTransactionsQuery,
  TransactionsViewQuery,
  TransactionsViewWithCryptoTransfersQuery,
  InternalSavingsPlanTransactionState,
  StakingTransactionsViewQuery,
} from '@bts-web/data-layer/server';
import Link from 'next/link';
import { ComponentProps, useMemo } from 'react';
import {
  formatAssetAmount,
  formatFiatAmount,
  getAssetAmountText,
  getFiatAmountSign,
  getTransactionTime,
} from './transactionItemUtils';
import { TransactionCell } from '../TransactionCell/TransactionCell.use-client';
import { useTransactionTitle } from '../utils/getTransactionTitle';
import {
  isSavingsPlanTransaction,
  TransactionDirection,
  TransactionItemTranslationsKeys,
  transactionTypeByTypeName,
  TransactionTypes,
  transactionTypesWithDetail,
} from '../types/transactions';
import { Chip } from '../../common/components/Chip/Chip';
import { useIsBelowScreenWidth } from '../../layouts/utils/useIsBelowScreenWidth';
import { ButtonBase, HTMLRenderer } from '../../common';
import { getAppConfig } from '@bts-web/core-app-config';
import { openTransactionDetailsDesktopDrawer } from './openTransactionDetailsDesktopDrawer';
import { TransactionItemImageIcon } from './TransactionItemImageIcon';
import { useModalDrawerContext } from '../../common/components/ModalDrawerContext/ModalDrawerContext';
import {
  SavingsPlansListTranslationsKeys,
  SavingsPlansTransactionsTranslationsKeys,
} from '../../savings-plans/types';
import { TransactionsTranslationsKeysResult } from '../utils/getTransactionsTranslations';
import { RegularChevronRight } from '../../common/svgIcons';

export type SavingsPlanTransactionItemType = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        SavingsPlansTransactionsQuery['response']['savingsPlanTransactions']
      >['edges']
    >[number]
  >['node']
>;

export type StakingTransactionItemType = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        StakingTransactionsViewQuery['response']['stakingTransactions']
      >['edges']
    >[number]
  >['node']
>;

export type TransactionEntryType = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<TransactionsViewQuery['response']['transactions']>['edges']
    >[number]
  >['node']
>;

export type TransactionCryptoTransferEntryType = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        TransactionsViewWithCryptoTransfersQuery['response']['cryptoTransfersTransactions']
      >['edges']
    >[number]
  >['node']
>;

export type AllTypesTransaction =
  | SavingsPlanTransactionItemType
  | TransactionEntryType
  | TransactionCryptoTransferEntryType;

const isClassicTransaction = (
  transaction: AllTypesTransaction,
): transaction is TransactionEntryType => {
  return (transaction && 'direction' in transaction) ?? false;
};

const isSavingsPlansTransactionFromSavingsPlansPage = (
  transaction: AllTypesTransaction,
): transaction is SavingsPlanTransactionItemType => {
  return transaction && 'state' in transaction ? true : false;
};

export interface TransactionItemProps<T> {
  transaction: AllTypesTransaction;
  showStatusChip?: boolean;
  showCategoryTypeChip?: boolean;
  useSimpleTitle?: boolean;
  translations: T;
  locale: string;
  className?: string;
}

export type AllTranslations =
  | TransactionItemTranslationsKeys
  | (SavingsPlansListTranslationsKeys &
      SavingsPlansTransactionsTranslationsKeys)
  | SavingsPlansTransactionsTranslationsKeys
  | TransactionsTranslationsKeysResult;

export const TransactionItem = ({
  transaction,
  showCategoryTypeChip,
  useSimpleTitle,
  translations,
  locale,
}: TransactionItemProps<AllTranslations>) => {
  const stateTranslations: Record<InternalSavingsPlanTransactionState, string> =
    {
      CONFIRMED: translations.confirmed,
      FAILED: translations.failed,
      FINISHED: translations.finished,
      SCHEDULED: translations.scheduled,
    };

  const isMobile = useIsBelowScreenWidth(1024);

  const { openDrawer } = useModalDrawerContext();

  const transactionType: TransactionTypes =
    transactionTypeByTypeName[
      transaction?.__typename as keyof typeof transactionTypeByTypeName
    ];

  const isFiatTransfer =
    'category' in transaction && transaction.category === 'FIAT_TRANSFER';

  const href: string | null = useMemo(() => {
    if (isFiatTransfer) {
      return `/transactions/${transaction?.id}?transactionType=${transactionTypeByTypeName.DepositTransactionFiatTransfer}`;
    }

    if (!transactionTypesWithDetail.includes(transactionType)) {
      return null;
    }

    const isFailedOrConfirmedOrScheduledSavingsPlan =
      (isSavingsPlanTransaction(transactionType) &&
        ((transaction as SavingsPlanTransactionItemType)?.state === 'FAILED' ||
          (transaction as SavingsPlanTransactionItemType)?.state ===
            'CONFIRMED')) ||
      (transaction as SavingsPlanTransactionItemType)?.state === 'SCHEDULED';

    if (isFailedOrConfirmedOrScheduledSavingsPlan) {
      return null;
    }

    return `/transactions/${transaction?.parentId}?transactionType=${transactionType}`;
  }, [transaction, transactionType]);

  const {
    isOutgoing,
    formattedDate,
    fiatAmountText,
    assetAmountText,
    incomingTransaction,
    outgoingTransaction,
    savingsPlanTransactionState,
    stateColor,
  } = useMemo(() => {
    const amountLabel = translations.grams;

    const assetAmount = formatAssetAmount(transaction?.assetAmount, locale);

    let assetAmountText =
      transaction?.asset && transaction.asset.symbol
        ? getAssetAmountText(
            assetAmount,
            amountLabel,
            transaction.asset.__typename,
            transaction.asset.symbol,
          )
        : '';

    const hasChildrenTransactions = 'childrenTransactions' in transaction;

    const childrenTransactions = hasChildrenTransactions
      ? transaction.childrenTransactions
      : null;

    const incomingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.INCOMING,
        )) ||
      null;

    const outgoingTransaction =
      (childrenTransactions &&
        childrenTransactions?.length > 1 &&
        childrenTransactions.find(
          (item) => item.direction === TransactionDirection.OUTGOING,
        )) ||
      null;

    const isOutgoing = isClassicTransaction(transaction)
      ? transaction?.direction === TransactionDirection.OUTGOING
      : false;

    const hasSavingsPlanTransactionState =
      transaction &&
      isSavingsPlansTransactionFromSavingsPlansPage(transaction) &&
      (transaction as SavingsPlanTransactionItemType)?.state;

    const savingsPlanTransactionState = hasSavingsPlanTransactionState
      ? stateTranslations[
          transaction.state as InternalSavingsPlanTransactionState
        ]
      : undefined;

    const stateColorMap: Record<
      InternalSavingsPlanTransactionState,
      ComponentProps<typeof TransactionCell>['visual']
    > = {
      CONFIRMED: 'success',
      FAILED: 'error',
      FINISHED: 'success',
      SCHEDULED: 'warning',
    };

    const stateColor = hasSavingsPlanTransactionState
      ? stateColorMap[
          (transaction as SavingsPlanTransactionItemType)
            .state as InternalSavingsPlanTransactionState
        ]
      : 'default';

    const { currency } = getAppConfig();

    let fiatAmountText =
      transaction?.fiatAmount?.value && parseFloat(transaction.fiatAmount.value)
        ? formatFiatAmount(
            transaction?.fiatAmount,
            getFiatAmountSign(transactionType, isOutgoing),
            {
              locale,
              currency,
            },
          )
        : '';

    const transactionInstant = isClassicTransaction(transaction)
      ? transaction?.time
      : transaction?.createdAt;

    const formattedDate = transactionInstant
      ? getTransactionTime({
          locale,
          time: transactionInstant,
        })
      : '';

    if (childrenTransactions) {
      const incomingAssetAmount = formatAssetAmount(
        incomingTransaction?.assetAmount,
        locale,
      );

      const outGoingAssetAmount = formatAssetAmount(
        outgoingTransaction?.assetAmount,
        locale,
      );

      fiatAmountText = `+${getAssetAmountText(
        incomingAssetAmount,
        amountLabel,
        incomingTransaction?.asset?.__typename as string,
        incomingTransaction?.asset?.symbol as string,
      )}`;

      assetAmountText = `-${getAssetAmountText(
        outGoingAssetAmount,
        amountLabel,
        outgoingTransaction?.asset?.__typename as string,
        outgoingTransaction?.asset?.symbol as string,
      )}`;
    }

    return {
      formattedDate,
      fiatAmountText,
      assetAmountText,
      isOutgoing,
      incomingTransaction,
      outgoingTransaction,
      transactionType,
      savingsPlanTransactionState,
      stateColor,
    };
  }, [locale, transaction, transactionType]);

  const { title, tagValue } = useTransactionTitle({
    transactionType,
    symbol: isFiatTransfer
      ? getAppConfig().currency
      : outgoingTransaction?.asset?.symbol || transaction.asset?.symbol,
    symbolTo: incomingTransaction?.asset?.symbol || null,
    translations,
    useSimpleTitle,
  });

  const clickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    e.stopPropagation();

    openTransactionDetailsDesktopDrawer(
      transaction as TransactionEntryType,
      openDrawer,
    );
  };

  const transactionCell = (
    <TransactionCell
      id={transaction.id}
      onClick={undefined}
      aria-label={`transaction ${transaction?.id}`}
      startIconSlot={
        <TransactionItemImageIcon
          imageUrl={
            incomingTransaction?.asset?.logoUrl || transaction?.asset?.logoUrl
          }
          isFiat={isFiatTransfer}
          imageAlt={
            incomingTransaction?.asset?.name ||
            transaction?.asset?.name ||
            transaction?.__typename
          }
          transactionType={transactionType}
        />
      }
      endIconSlot={href && <RegularChevronRight size={18} data-dir="rtl" />}
      disabled={!href}
      key={transaction?.id as string}
      title={
        useSimpleTitle ? (
          (transaction.asset?.name ?? '')
        ) : (
          <HTMLRenderer>{title ?? ''}</HTMLRenderer>
        )
      }
      transactionType={transactionType}
      subtitle={formattedDate}
      fiatAmountText={fiatAmountText}
      assetAmountText={assetAmountText}
      direction={
        isOutgoing
          ? TransactionDirection.OUTGOING
          : TransactionDirection.INCOMING
      }
      tagContent={
        tagValue && showCategoryTypeChip ? <Chip label={tagValue} /> : null
      }
      transactionStatus={savingsPlanTransactionState}
      visual={stateColor}
    />
  );

  const MobileWrapperElement = !href ? 'li' : Link;

  return isMobile && href ? (
    <MobileWrapperElement
      data-testid="transaction-cell-link"
      href={href}
      prefetch={false}
    >
      {transactionCell}
    </MobileWrapperElement>
  ) : (
    <ButtonBase
      fullWidth
      noPadding
      visual="ghost"
      type="button"
      data-testid="transaction-cell-button"
      onClick={clickHandler}
      disabled={!href}
    >
      {transactionCell}
    </ButtonBase>
  );
};
